import { t } from 'i18next';
import { getHtmlCodeIndexableText } from './modules/importedCode';
import { ComponentType } from 'react';
import { ModuleContent, ModuleData } from './contract';
import { getButtonModuleIndexableText } from './modules/button';
import { getConfirmReadModuleIndexableText } from './modules/confirmRead';
import { getCookieButtonModuleIndexableText } from './modules/cookieButton';
import { getWelcomeGuideButtonModuleIndexableText } from './modules/welcomeGuideButton';
import { getTextModuleIndexableText } from './modules/text';
import { getImageModuleIndexableText } from './modules/image';
import { getAnimatedSvgModuleIndexableText } from './modules/animatedSvg';
import { getBizAgentModuleIndexableText } from './modules/bizAgent';
import { getVideoModuleIndexableText } from './modules/video';
import { getObjectListModuleIndexableText } from './modules/objectList';

/**
 * Props that every module renderer component receives.
 */
export type ModuleComponentProps = {
	module: ModuleData;
};

/**
 * Defines a module type that can be rendered in the container system.
 * New module types are added by calling registerModuleType().
 */
export type ModuleTypeDefinition = {
	/** Unique key that identifies this module type (e.g. 'Text', 'Image'). */
	type: string;
	/** Human-readable display name. */
	name: string;
	/** Optional CSS classes added to the outer module wrapper. */
	wrapperClasses?: string[];
	/**
	 * React component that renders this module type.
	 * If not provided, the module type will be silently skipped during rendering.
	 */
	component?: ComponentType<ModuleComponentProps>;
	/**
	 * Optional function that extracts indexable text from the module content.
	 * Used by getIndexableContent() for search indexing.
	 */
	getIndexableText?: (content: ModuleContent) => string;
	/** Whether this module type can be copied in the admin UI. Defaults to true. */
	canCopy?: boolean;
};

const registry = new Map<string, ModuleTypeDefinition>();

/**
 * Registers a module type definition.
 * Throws if a module type with the same key is already registered.
 */
export function registerModuleType(definition: ModuleTypeDefinition): void {
	if (registry.has(definition.type)) {
		throw new Error(`Module type "${definition.type}" is already registered.`);
	}
	registry.set(definition.type, definition);
}

/**
 * Returns the definition for a registered module type, or undefined if not found.
 */
export function getModuleTypeDefinition(type: string): ModuleTypeDefinition | undefined {
	return registry.get(type);
}

/**
 * Returns all registered module type definitions.
 */
export function getAllModuleTypeDefinitions(): ModuleTypeDefinition[] {
	return Array.from(registry.values());
}

/**
 * Returns true if the given module type is registered.
 */
export function isModuleTypeRegistered(type: string): boolean {
	return registry.has(type);
}

/**
 * Associates a React component with an already-registered module type.
 * This allows the component layer to register rendering components
 * without creating circular dependencies with the types layer.
 * Throws if the module type is not registered.
 */
export function registerModuleComponent(type: string, component: ComponentType<ModuleComponentProps>): void {
	const definition = registry.get(type);
	if (!definition) {
		throw new Error(`Cannot register component: module type "${type}" is not registered.`);
	}
	definition.component = component;
}

// ---------------------------------------------------------------------------
// Built-in module types
// ---------------------------------------------------------------------------

registerModuleType({
	type: 'HtmlCode',
	get name() {
		return t('HTML_IMPORT.HTML_CODE');
	},
	getIndexableText: getHtmlCodeIndexableText,
});
registerModuleType({
	type: 'SharedCss',
	get name() {
		return t('HTML_IMPORT.SHARED_CSS');
	},
	wrapperClasses: ['biz-module-wrapper--shared-css'],
});
registerModuleType({
	type: 'Text',
	name: 'Text',
	getIndexableText: getTextModuleIndexableText,
});

registerModuleType({
	type: 'Button',
	name: 'Button',
	getIndexableText: getButtonModuleIndexableText,
	wrapperClasses: ['biz-module-wrapper--button'],
});

registerModuleType({
	type: 'ConfirmRead',
	name: 'Confirm Read',
	getIndexableText: getConfirmReadModuleIndexableText,
});

registerModuleType({
	type: 'CookieButton',
	name: 'Cookie Button',
	getIndexableText: getCookieButtonModuleIndexableText,
	wrapperClasses: ['biz-module-wrapper--button'],
});

registerModuleType({
	type: 'WelcomeGuideButton',
	name: 'Welcome Guide Button',
	getIndexableText: getWelcomeGuideButtonModuleIndexableText,
	wrapperClasses: ['biz-module-wrapper--button'],
});

registerModuleType({
	type: 'Image',
	name: 'Image',
	getIndexableText: getImageModuleIndexableText,
});

registerModuleType({
	type: 'Divider',
	name: 'Divider',
});

registerModuleType({
	type: 'AnimatedSvg',
	name: 'Animated SVG',
	getIndexableText: getAnimatedSvgModuleIndexableText,
});

registerModuleType({
	type: 'BizAgent',
	name: 'AI Chat',
	getIndexableText: getBizAgentModuleIndexableText,
});

registerModuleType({
	type: 'Video',
	name: 'Video',
	getIndexableText: getVideoModuleIndexableText,
});

registerModuleType({
	type: 'IFrame',
	name: 'IFrame',
	wrapperClasses: ['biz-module-wrapper--iframe'],
});

registerModuleType({
	type: 'Segment',
	name: 'Segment',
	// Segments have no direct text content; their children are indexed recursively.
});

registerModuleType({
	type: 'Form',
	name: 'Form',
	canCopy: false,
	// Forms have no directly indexable text; content is loaded separately from the form API.
});

registerModuleType({
	type: 'ObjectList',
	name: 'Object list',
	getIndexableText: getObjectListModuleIndexableText,
});
